/**
 * Inline "Did you mean…?" suggestions while adding pantry items.
 */
import { useEffect, useMemo, useState } from 'react'
import { authenticatedFetch } from '../../utils/auth.js'
import {
  applyPantrySpellingToInput,
  getPantryAddSegments,
  parseJsonResponse,
} from './pantryHelpers.js'

const DEBOUNCE_MS = 350

export default function PantrySpellingSuggestions({
  inputValue,
  onApplySuggestion,
  className = '',
}) {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)

  const segments = useMemo(() => getPantryAddSegments(inputValue), [inputValue])
  const segmentsKey = segments.join('\n')

  useEffect(() => {
    if (segments.length === 0) {
      setSuggestions([])
      setLoading(false)
      return undefined
    }

    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({ names: segments.join(',') })
        const res = await authenticatedFetch(`/inventory/suggest-spelling?${params.toString()}`, {
          signal: controller.signal,
        })
        if (!res.ok) {
          setSuggestions([])
          return
        }
        const data = await parseJsonResponse(res)
        const rows = Array.isArray(data?.suggestions) ? data.suggestions : []
        setSuggestions(rows.filter((row) => row?.input && row?.suggestion))
      } catch (err) {
        if (err?.name !== 'AbortError') setSuggestions([])
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [segmentsKey, segments])

  if (!inputValue.trim() || suggestions.length === 0) {
    return null
  }

  return (
    <div className={`pantry-spelling-suggestions ${className}`.trim()} role="status" aria-live="polite">
      {suggestions.map((row) => (
        <div key={row.input} className="pantry-spelling-suggestions__row">
          <span className="pantry-spelling-suggestions__label">
            Did you mean{' '}
            <button
              type="button"
              className="pantry-spelling-suggestions__choice"
              onClick={() => onApplySuggestion(applyPantrySpellingToInput(inputValue, row.input, row.suggestion))}
            >
              {row.suggestion}
            </button>
            {segments.length > 1 ? (
              <span className="pantry-spelling-suggestions__for"> for &ldquo;{row.input}&rdquo;</span>
            ) : null}
            ?
          </span>
        </div>
      ))}
      {loading ? <span className="pantry-spelling-suggestions__loading">Checking spelling…</span> : null}
    </div>
  )
}
